package org.vreo.devicehub.features.deviceDetails.presentation

import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.onStart
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.coroutines.Job

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import org.vreo.devicehub.features.core.data.appLogger.AppLogger

import org.vreo.devicehub.features.core.domain.DataError
import org.vreo.devicehub.features.core.domain.EmptyResult
import org.vreo.devicehub.features.core.domain.device.device.AnprDevice
import org.vreo.devicehub.features.core.domain.device.device.DeviceAuthentication
import org.vreo.devicehub.features.core.domain.device.device.ProductType
import org.vreo.devicehub.features.core.domain.onError
import org.vreo.devicehub.features.core.domain.onSuccess
import org.vreo.devicehub.features.core.presentation.getFormatedDate
import org.vreo.devicehub.features.core.presentation.getFormatedTime
import org.vreo.devicehub.features.core.presentation.toUiText
import org.vreo.devicehub.features.deviceDetails.domain.DeviceDetailsRepository

/**
 * Device details screen view model. Loads the currently authenticated device's details from
 * [repository] and exposes them as [DeviceDetailsState] for [DeviceDetailsScreenView]. Also
 * handles updating the device's tag, unlinking it from the current user's account, and (for
 * admins only) permanently deleting it from the production device registry.
 *
 * @property authentication current device/user authentication, providing the token and device ID to load
 * @property repository source of device details
 * @property isAdmin whether the current user is allowed to permanently delete the device from production
 */
class DeviceDetailsViewModel(
    private val authentication: DeviceAuthentication,
    private val repository: DeviceDetailsRepository,
    val isAdmin: Boolean = false,
) : ViewModel() {
    private var refreshJob: Job? = null

    private val _state = MutableStateFlow(DeviceDetailsState())

    /**
     * Current [DeviceDetailsState], shared across subscribers. Triggers an initial
     * [refreshDeviceDetails] when first collected.
     */
    val state = _state
        .onStart { refreshDeviceDetails() }
        .stateIn(
            scope = viewModelScope,
            started = SharingStarted.WhileSubscribed(5000L),
            initialValue = _state.value
        )

    /**
     * Handle a single [DeviceDetailsActions] emitted by the UI.
     */
    fun onAction(action: DeviceDetailsActions) {
        when (action) {
            is DeviceDetailsActions.OnClearError -> _state.update { it.copy(error = null) }
            is DeviceDetailsActions.Refresh -> refreshDeviceDetails()
            is DeviceDetailsActions.OnChangeEditTagEnable -> _state.update { it.copy(editingTag = action.value) }
            is DeviceDetailsActions.OnChangeEditTag -> _state.update { it.copy(temporarilyTag = action.value) }
            is DeviceDetailsActions.OnSaveTag -> saveTag()
            is DeviceDetailsActions.OnUnlinkClicked -> _state.update { it.copy(showUnlinkDialog = true) }
            is DeviceDetailsActions.OnClearUnlinkDialog -> _state.update { it.copy(showUnlinkDialog = false) }
            is DeviceDetailsActions.OnUnlinkDeviceConfirmed -> unlinkDevice()
            is DeviceDetailsActions.OnDeleteClicked -> _state.update { it.copy(showDeleteDialog = true) }
            is DeviceDetailsActions.OnClearDeleteDialog -> _state.update { it.copy(showDeleteDialog = false) }
            is DeviceDetailsActions.OnDeleteConfirmed -> deleteDeviceFromProduction()
        }
    }

    /**
     * Loads the current device's details from [repository], cancelling any in-flight load, and
     * updates [state] with the resulting data or error.
     */
    private fun refreshDeviceDetails() {
        refreshJob?.cancel()
        refreshJob = viewModelScope.launch {
            _state.update { it.copy(isLoading = true) }

            try {
                repository.getDeviceDetails(
                    authentication.userToken,
                    authentication.deviceId
                ).onSuccess { data ->
                    val lastActiveTime = if (data.lastActivityTime != null) {
                        "${getFormatedDate(data.lastActivityTime)} ${getFormatedTime(data.lastActivityTime)}"
                    } else {
                        "N/A"
                    }

                    _state.update {
                        it.copy(
                            isLoading = false,

                            isDeviceActive = data.active,
                            deviceId = data.deviceId,
                            productType = data.device,
                            serialNumber = data.serialNumber,
                            softwareVersion = data.softwareVersion,
                            ocrVersion = data.ocrVersion,
                            localIpAddress = data.localIpAddress,
                            macAddress = data.macAddress,
                            lastActivityTime = lastActiveTime,
                            userTag = data.userTag,
                            linkingPassword = data.linkinPassword
                        )
                    }
                }.onError { error ->
                    _state.update {
                        it.copy(
                            isLoading = false,
                            error = error.toUiText(),

                            isDeviceActive = false,
                            deviceId = authentication.deviceId,
                            productType = null,
                            serialNumber = "",
                            softwareVersion = "",
                            ocrVersion = "",
                            localIpAddress = "",
                            macAddress = "",
                            lastActivityTime = "",
                            userTag = "",
                            linkingPassword = ""
                        )
                    }
                }
            } catch (e: CancellationException) {
                throw e
            } catch (_: Exception) {
                _state.update { it.copy(isLoading = false, error = DataError.Local.UNKNOWN.toUiText()) }
            }
        }
    }

    /** Unlinks the current device from the user's account and dismisses the unlink dialog. */
    private fun unlinkDevice() = performDeviceAction(
        clearDialog = { it.copy(showUnlinkDialog = false) },
        action = repository::unlinkDevice,
    )

    /**
     * Permanently deletes the current device from the production registry and dismisses the
     * delete dialog. No-ops if [isAdmin] is `false`.
     */
    private fun deleteDeviceFromProduction() {
        if (!isAdmin) return

        performDeviceAction(
            clearDialog = { it.copy(showDeleteDialog = false) },
            action = repository::deleteDeviceFromProduction,
        )
    }

    /**
     * Guards mutating device operations (tag update, unlink, delete): fails fast if a
     * [refreshDeviceDetails] is currently in flight or the device is not active, updating
     * [state] with the corresponding error in that case. On success, marks [state] as loading.
     *
     * @return a snapshot of [state] taken before this call if the operation may proceed, or
     * `null` if a guard failed and the caller should abort
     */
    private fun guardActiveDeviceOperation(): DeviceDetailsState? {
        if (refreshJob?.isActive == true) {
            _state.update { it.copy(isLoading = false, error = DataError.Local.INVALID_STATE.toUiText()) }
            return null
        }

        val currentState = _state.value
        if (!currentState.isDeviceActive || currentState.productType == null) {
            AppLogger.error("Device is not active, ID: ${authentication.deviceId}")
            _state.update { it.copy(isLoading = false, error = DataError.Remote.SERVICE_UNAVAILABLE.toUiText()) }
            return null
        }

        _state.update { it.copy(isLoading = true) }
        return currentState
    }

    /**
     * Persists [DeviceDetailsState.temporarilyTag] as the device's new user tag via [repository],
     * updating [state] with the result. No-ops if [guardActiveDeviceOperation] blocks the call.
     */
    private fun saveTag() = viewModelScope.launch {
        val currentState = guardActiveDeviceOperation() ?: return@launch

        try {
            repository.updateDeviceDetails(
                authentication.userToken,
                AnprDevice(
                    deviceId = authentication.deviceId,
                    device = currentState.productType
                        ?: ProductType.VoltS1PoleShortRange,  // This field is ignored for pre-configured devices
                    serialNumber = currentState.serialNumber,
                    softwareVersion = currentState.softwareVersion,
                    ocrVersion = currentState.ocrVersion,
                    localIpAddress = currentState.localIpAddress,
                    macAddress = currentState.macAddress,
                    active = currentState.isDeviceActive,
                    userTag = currentState.temporarilyTag
                )
            )
                .onSuccess {
                    _state.update {
                        it.copy(
                            isLoading = false,
                            userTag = currentState.temporarilyTag,
                            editingTag = false
                        )
                    }
                }
                .onError { error ->
                    _state.update { it.copy(isLoading = false, error = error.toUiText()) }
                }
        } catch (e: CancellationException) {
            throw e
        } catch (_: Exception) {
            _state.update { it.copy(isLoading = false, error = DataError.Local.UNKNOWN.toUiText()) }
        }
    }

    /**
     * Runs a mutating device [action] against [repository] behind [guardActiveDeviceOperation],
     * updating [state] with the result and clearing the caller's confirmation dialog via
     * [clearDialog] regardless of outcome.
     *
     * @param clearDialog dismisses the confirmation dialog associated with this action
     * @param action repository call to perform, given the user token and device ID
     */
    private fun performDeviceAction(
        clearDialog: (DeviceDetailsState) -> DeviceDetailsState,
        action: suspend (token: String, deviceId: String) -> EmptyResult<DataError.Remote>,
    ) = viewModelScope.launch {
        if (guardActiveDeviceOperation() == null) return@launch

        try {
            action(authentication.userToken, authentication.deviceId)
                .onSuccess {
                    _state.update { clearDialog(it).copy(isLoading = false) }
                }
                .onError { error ->
                    _state.update { clearDialog(it).copy(isLoading = false, error = error.toUiText()) }
                }
        } catch (e: CancellationException) {
            throw e
        } catch (_: Exception) {
            _state.update { clearDialog(it).copy(isLoading = false, error = DataError.Local.UNKNOWN.toUiText()) }
        }
    }
}
